import {
  WizardBottomBar,
  WizardBottomBarPrimaryButton,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentBody,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardPageSingleColumnRow,
} from '@/components/Wizard'
import styles from '../MeasureOne.module.css'
import { lazy, Suspense, useMemo } from 'react'
import { useOutletContext } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { links } from '@/utils/link'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { MeasureOneKicker } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneKicker'
import { observer } from 'mobx-react-lite'
import { Id } from '@/infra/id'
import env from '@/infra/env'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button } from '@/components/Button'
import { MeasureOneSpinner } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneSpinner'
import { useFeatures } from '@/contexts/root-store-context'
import { take } from 'rxjs/operators'
import { useObservableValue } from '@/hooks/rx-hooks'
import { map } from 'rxjs'

const loadMeasureOneWidget = () => {
  return new Promise<void>((resolve, reject) => {
    if (customElements.get('m1-link')) {
      return resolve()
    }

    const script = document.createElement('script')
    script.src = `https://${env.MEASUREONE_HOST_NAME}/v3/js/m1-link-2021042000.js`
    script.async = true
    script.onload = () => resolve()
    script.onerror = reject
    document.body.appendChild(script)
  })
}

const MeasureOneWidget = lazy(async () => {
  await loadMeasureOneWidget()
  return import('../../components/MeasureOneWidget').then((module) => ({
    default: module.MeasureOneWidget,
  }))
})

export default observer(function MeasureOneConnectionContainer() {
  const vm = useOutletContext<JobDetailsViewModel>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm
  const { colorScheme$ } = useFeatures(({ shell }) => ({
    colorScheme$: shell.colorScheme$,
  }))

  const config = useObservableValue(
    useMemo(
      () =>
        colorScheme$.pipe(
          take(1),
          map((mode) => {
            const config = measureOneVm.measureOneConfig

            if (mode === 'light') {
              return {
                ...config,
                branding: {
                  styles: {
                    isDark: false,
                    dark_background: '',
                  },
                },
              }
            }

            return {
              ...config,
              branding: {
                styles: {
                  isDark: true,
                  dark_background: getComputedStyle(document.documentElement)
                    .getPropertyValue('--color-neutral-800')
                    .trim(),
                },
              },
            }
          }),
        ),
      [colorScheme$, measureOneVm],
    ),
  )

  return (
    <>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <div className={styles.content}>
            <WizardContentHeader>
              <MeasureOneKicker />

              <WizardContentHeaderTitle>
                Setting up your connection
              </WizardContentHeaderTitle>
              <WizardContentHeaderDescription>
                Use the MeasureOne flow to search for your provider, enter your
                credentials, and finish the setup
              </WizardContentHeaderDescription>
            </WizardContentHeader>
          </div>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentBody>
            <ConnectionBody
              jobId={Id.assert(vm.job.id)}
              config={JSON.stringify(config)}
              onAddDataSource={measureOneVm.connectJobTaxDataImport}
              onTokenExpired={measureOneVm.generateAccessToken}
            />
          </WizardContentBody>

          {measureOneVm.canShowBottomBar && (
            <WizardBottomBar progress={0}>
              <WizardBottomBarSpacer />
              <WizardBottomBarSection>
                <ButtonGroup>
                  <Button
                    to={links.measureOneSelectExperienceType(vm.job.id)}
                    variant="outline"
                    label="Add another account"
                  />
                  <WizardBottomBarPrimaryButton
                    isPending={!measureOneVm.canNavigateToImportScreen}
                    isDisabled={!measureOneVm.canNavigateToImportScreen}
                    to={links.measureOneSelectDocuments(vm.job.id)}
                    label="Select my documents"
                  />
                </ButtonGroup>
              </WizardBottomBarSection>
            </WizardBottomBar>
          )}
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>
    </>
  )
})

interface ConnectionBodyProps {
  jobId: string
  config: string
  onAddDataSource: (dataSourceId: string, dataSourceName: string) => void
  onTokenExpired: () => void
}

function ConnectionBody({
  config,
  jobId,
  onAddDataSource,
  onTokenExpired,
}: ConnectionBodyProps) {
  return (
    <Suspense fallback={<MeasureOneSpinner />}>
      <MeasureOneWidget
        onTokenExpired={onTokenExpired}
        jobId={Id.assert(jobId)}
        config={config}
        addDataSource={onAddDataSource}
      />
    </Suspense>
  )
}
